import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, inr, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { ShieldCheck, Truck, CreditCard } from "lucide-react";

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    full_name: user?.name && user.name !== "Guest" ? user.name : "",
    phone: user?.phone || "", email: user?.email || "",
    line1: "", line2: "", city: "", state: "", pincode: "",
  });
  const [payment, setPayment] = useState("ONLINE");
  const [placing, setPlacing] = useState(false);
  const [isB2b, setIsB2b] = useState(false);
  const [gstin, setGstin] = useState("");
  const [businessName, setBusinessName] = useState("");

  const shipping = subtotal >= 800 ? 0 : 50;
  const total = subtotal + shipping;

  if (items.length === 0) {
    return <div className="max-w-2xl mx-auto px-4 py-24 text-center"><p className="text-muted-foreground mb-4">Your bag is empty.</p><Button onClick={() => navigate("/shop")} className="bg-rose-950">Shop Now</Button></div>;
  }

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validate = () => {
    for (const [k, label] of [["full_name", "Full name"], ["phone", "Phone"], ["line1", "Address"], ["city", "City"], ["state", "State"], ["pincode", "Pincode"]]) {
      if (!form[k]?.trim()) { toast.error(`${label} is required`); return false; }
    }
    if (form.phone.replace(/\D/g, "").length < 10) { toast.error("Enter a valid phone number"); return false; }
    if (form.pincode.length !== 6) { toast.error("Enter a valid 6-digit pincode"); return false; }
    if (isB2b && gstin.trim().length !== 15) { toast.error("Valid 15-digit GSTIN required for business (B2B) orders"); return false; }
    return true;
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const placeOrder = async () => {
    if (!validate()) return;
    setPlacing(true);
    try {
      const { data: pay } = await api.post("/payments/create", { amount: total });
      if (!pay.configured || pay.gateway !== "razorpay" || !pay.order_id) {
        toast.error("Online payment is temporarily unavailable. Please try again shortly.");
        setPlacing(false);
        return;
      }
      const loaded = await loadRazorpayScript();
      if (!loaded || !window.Razorpay) {
        toast.error("Could not load payment. Check your connection and retry.");
        setPlacing(false);
        return;
      }
      const options = {
        key: pay.key_id,
        amount: pay.amount,
        currency: "INR",
        name: "Million Pixels",
        description: `Order Payment (${items.length} items)`,
        order_id: pay.order_id,
        prefill: { name: form.full_name, contact: form.phone, email: form.email || "" },
        theme: { color: "#800020" },
        handler: async function (response) {
          try {
            const payload = {
              items: items.map((i) => ({ product_id: i.product_id, name: i.name, sku: i.sku, size: i.size, color: i.color, qty: i.qty, price: i.price, image: i.image })),
              address: form,
              payment_mode: "ONLINE",
              payment_gateway: "razorpay",
              payment_id: response.razorpay_payment_id,
              subtotal, shipping, discount: 0, total,
              is_b2b: isB2b, gstin, business_name: businessName,
            };
            const { data } = await api.post("/orders", payload);
            clear();
            toast.success("Payment Successful! Order placed & WhatsApp notified.");
            navigate(`/order-success/${data.order.order_number}`);
          } catch (err) {
            toast.error("Payment captured but order save failed. Please contact support with your payment ID.");
          }
        },
        modal: { ondismiss: function () { toast.info("Payment cancelled. You can retry."); setPlacing(false); } },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", function (response) {
        toast.error(`Payment failed: ${response.error?.description || "Unknown error"}. Please retry.`);
        setPlacing(false);
      });
      rzp.open();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to place order");
      setPlacing(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-12 py-10">
      <h1 className="font-serif text-3xl lg:text-4xl font-bold text-rose-950 mb-8">Checkout</h1>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
        <div className="lg:col-span-3 space-y-8">
          <div>
            <h2 className="font-serif text-xl font-semibold text-rose-950 mb-4">Shipping Details</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label>Full Name</Label><Input value={form.full_name} onChange={set("full_name")} className="mt-1.5 h-11" data-testid="checkout-name" /></div>
              <div><Label>WhatsApp Mobile</Label><Input value={form.phone} onChange={set("phone")} placeholder="+91..." className="mt-1.5 h-11" data-testid="checkout-phone" /></div>
              <div className="sm:col-span-2"><Label>Email (optional)</Label><Input value={form.email} onChange={set("email")} className="mt-1.5 h-11" data-testid="checkout-email" /></div>
              <div className="sm:col-span-2"><Label>Address Line 1</Label><Input value={form.line1} onChange={set("line1")} className="mt-1.5 h-11" data-testid="checkout-line1" /></div>
              <div className="sm:col-span-2"><Label>Address Line 2 (optional)</Label><Input value={form.line2} onChange={set("line2")} className="mt-1.5 h-11" data-testid="checkout-line2" /></div>
              <div><Label>City</Label><Input value={form.city} onChange={set("city")} className="mt-1.5 h-11" data-testid="checkout-city" /></div>
              <div><Label>State</Label><Input value={form.state} onChange={set("state")} className="mt-1.5 h-11" data-testid="checkout-state" /></div>
              <div><Label>Pincode</Label><Input value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value.replace(/\D/g, "").slice(0, 6) })} className="mt-1.5 h-11" data-testid="checkout-pincode" /></div>
            </div>
          </div>

          <div>
            <h2 className="font-serif text-xl font-semibold text-rose-950 mb-4">GST / Business Invoice (optional)</h2>
            <label className="flex items-center gap-3 border border-rose-900/15 rounded-xl p-4 cursor-pointer" data-testid="checkout-b2b-toggle">
              <input type="checkbox" checked={isB2b} onChange={(e) => setIsB2b(e.target.checked)} className="w-4 h-4 accent-rose-950" />
              <div><p className="font-medium text-sm">This is a business order (claim GST input credit)</p><p className="text-xs text-muted-foreground">Provide GSTIN to receive a B2B tax invoice</p></div>
            </label>
            {isB2b && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                <div><Label>GSTIN (15 digit)</Label><Input value={gstin} onChange={(e) => setGstin(e.target.value.toUpperCase().slice(0, 15))} className="mt-1.5 h-11" data-testid="checkout-gstin" /></div>
                <div><Label>Business Name</Label><Input value={businessName} onChange={(e) => setBusinessName(e.target.value)} className="mt-1.5 h-11" data-testid="checkout-business-name" /></div>
              </div>
            )}
          </div>

          <div>
            <h2 className="font-serif text-xl font-semibold text-rose-950 mb-4">Payment Method</h2>
            <div className="flex items-center gap-3 border border-rose-950 bg-rose-950/5 rounded-xl p-4" data-testid="payment-online">
              <CreditCard className="w-5 h-5 text-rose-950" />
              <div><p className="font-medium text-sm">Pay Online — Secure Prepaid (Razorpay / Cashfree)</p><p className="text-xs text-muted-foreground">UPI, Cards, Netbanking, Wallets · 100% secure · Auto-failover</p></div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">We are a prepaid-only store for a faster, safer delivery experience. Cash on Delivery is not available.</p>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="border border-rose-900/15 rounded-2xl p-6 sticky top-24 bg-white">
            <h2 className="font-serif text-xl font-semibold text-rose-950 mb-4">Order Summary</h2>
            <div className="space-y-3 max-h-60 overflow-y-auto mb-4">
              {items.map((i) => (
                <div key={i.key} className="flex gap-3">
                  <img src={i.image} className="w-14 h-16 object-cover rounded" alt="" />
                  <div className="flex-1 text-sm"><p className="font-medium leading-tight">{i.name}</p><p className="text-xs text-muted-foreground">{i.size}·{i.color}·Qty {i.qty}</p></div>
                  <span className="text-sm font-semibold">{inr(i.price * i.qty)}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-rose-900/10 pt-4 space-y-2 text-sm">
              <div className="flex justify-between"><span>Subtotal</span><span>{inr(subtotal)}</span></div>
              <div className="flex justify-between"><span>Shipping</span><span>{shipping === 0 ? "FREE" : inr(shipping)}</span></div>
              <div className="flex justify-between font-serif text-lg font-bold text-rose-950 pt-2 border-t border-rose-900/10"><span>Total</span><span data-testid="checkout-total">{inr(total)}</span></div>
            </div>
            <Button onClick={placeOrder} disabled={placing} className="w-full bg-rose-950 hover:bg-rose-900 h-12 mt-5 text-base rounded-full" data-testid="checkout-place-order-button">
              {placing ? "Placing Order..." : "Place Order"}
            </Button>
            <p className="text-xs text-center text-muted-foreground mt-3 flex items-center justify-center gap-1"><ShieldCheck className="w-3.5 h-3.5" />Secure checkout · WhatsApp confirmation</p>
          </div>
        </div>
      </div>
    </div>
  );
}
